import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiClient } from "../api/ApiContext.jsx";
import AuthLayout from "../layout/AuthLayout.jsx";
import { InlineMessage, errorText } from "../ui/Feedback.jsx";

/**
 * Not one of the 16 hand-off components: `POST /auth/password/reset/confirm`
 * exists in docs/panel-api.md but the hand-off set only ships the *request*
 * half (PasswordResetPage). Minimal form in the same token-based style.
 */
export default function ResetPasswordConfirmScreen() {
  const api = useApiClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  async function handleSubmit(event) {
    event.preventDefault();
    if (mismatch) return;
    setError(null);
    try {
      await api.auth.confirmPasswordReset({ token, password });
      setDone(true);
    } catch (err) {
      setError(errorText(err, "Could not set the new password."));
    }
  }

  return (
    <AuthLayout>
      {error ? <InlineMessage tone="danger">{error}</InlineMessage> : null}
      <div
        style={{
          background: "var(--surface-1)",
          borderRadius: "var(--radius-lg)",
          padding: "2rem",
        }}
      >
        {done ? (
          <>
            <p style={{ fontSize: "var(--text-xl)", fontWeight: 500, margin: "0 0 4px" }}>
              Password updated
            </p>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 20px" }}>
              You can log in with your new password now.
            </p>
            <button
              type="button"
              className="ical-btn ical-btn-primary"
              style={{ width: "100%" }}
              onClick={() => navigate("/login")}
            >
              Go to log in
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: "var(--text-xl)", fontWeight: 500, margin: "0 0 4px" }}>
              Set a new password
            </p>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 20px" }}>
              At least 12 characters, with a number and a special character.
            </p>
            {!token ? (
              <InlineMessage tone="warning">
                This link is missing its reset token. Request a new link from the reset page.
              </InlineMessage>
            ) : null}
            <form onSubmit={handleSubmit}>
              <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
                New password
              </label>
              <input
                className="ical-input"
                type="password"
                required
                minLength={12}
                style={{ width: "100%", marginBottom: 12 }}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={!token}
              />
              <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>
                Confirm password
              </label>
              <input
                className="ical-input"
                type="password"
                required
                style={{
                  width: "100%",
                  marginBottom: mismatch ? 4 : 20,
                  borderColor: mismatch ? "var(--fill-danger)" : undefined,
                }}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                disabled={!token}
              />
              {mismatch ? (
                <p style={{ fontSize: "var(--text-xs)", color: "var(--text-danger)", margin: "0 0 12px" }}>
                  Passwords don't match.
                </p>
              ) : null}
              <button
                type="submit"
                className="ical-btn ical-btn-primary"
                style={{ width: "100%" }}
                disabled={!token}
              >
                Save password
              </button>
            </form>
          </>
        )}
        <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", textAlign: "center", margin: "14px 0 0" }}>
          <button
            type="button"
            onClick={() => navigate("/login")}
            style={{ color: "var(--text-accent)", background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}
          >
            Back to log in
          </button>
        </p>
      </div>
    </AuthLayout>
  );
}
